/**
 * Siloed metadata accessors for branded clips.
 *
 * Mirrors what's in ../../Utilities but kept local so we can evolve the
 * branded version (additional fields, different defaults) without touching
 * the partner-shared utilities.
 */
import type { PlaylistClip } from "../../@types";

type MetaEntry = { key: string; value: string };

const readMeta = (clip: PlaylistClip | null, key: string): string => {
  if (!clip?.metadata) return "";
  const found = (clip.metadata as MetaEntry[]).find((m) => m?.key === key);
  return found?.value ?? "";
};

/**
 * Partner-api emits metadata keys with the `PARTNER_` prefix preserved on
 * the wire (e.g. `PARTNER_playerPhoto`, `PARTNER_teamLogo`). Earlier
 * comments here assumed the prefix was stripped — the live payload shows
 * otherwise, so every accessor below uses the prefixed key directly.
 */
export const getPlayerPhoto = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_playerPhoto");

export const getPlayerName = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_playerName") || clip?.username || "";

export const getPlayerFullName = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_playerFullName");

export const getPlayerCountry = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_playerCtryISO");

export const getTeamName = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_teamName");

export const getTeamLogo = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_teamLogo");

export const getTeamCountry = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_teamCtryISO");

/**
 * Opponent-team accessors. Added by partner-api PR #1057 (ALL-1059) — the
 * HLTV ingest pipeline writes opponent-side fields alongside the player's
 * own team. These were previously faked via
 * `buildMockMatchContext.opponentTeam*`; consumers can read them directly
 * now.
 */
export const getOpponentTeamName = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_opponentTeamName");

export const getOpponentTeamLogo = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_opponentTeamLogo");

export const getOpponentTeamCountry = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_opponentTeamCtryISO");

/**
 * "Game N of M" series label and "16 - 12" map scoreline — also from
 * partner-api PR #1057. Returned as strings exactly as the engine emits
 * them so callers don't have to know about HLTV's formatting.
 */
export const getSeriesGameLabel = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_seriesGameLabel");

export const getScoreLine = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_scoreLine");

export const getEventName = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_eventName");

export const getEventLogo = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_eventLogo");

export const getMatchId = (clip: PlaylistClip | null) =>
  readMeta(clip, "PARTNER_matchId");

/**
 * Display tags shown on the bottom-playlist row's MetaRow.
 *
 * Three tag families get derived / surfaced here, each in display form
 * (what the row chip should render, not the raw partner-api value):
 *
 *   1. Kill-count       — from CS_Kill Count (raw "2"..."5").
 *                         "5" → "ACE", "4" → "4K", "3" → "3K", "2" → "2K".
 *                         The whitelist used to contain only the literal
 *                         "4K" / "Ace" strings, which never matched the
 *                         raw numeric value the engine emits, so kill-count
 *                         tags were silently missing on every row.
 *   2. Situational      — from CS_Situational (multi-emit; e.g. "1 Tap",
 *                         "Wallbang", "Flashed", "Quickscope", "All
 *                         Headshots", "No Scope", "Clutch"). Whitelist
 *                         expanded to cover the actual partner-api values
 *                         we see in the wire payload, not just the legacy
 *                         allowedValuesCS2 set.
 *   3. Headshot count   — from CS_Headshots (raw "2"/"3"). "3" → "3 HS",
 *                         "2" → "2 HS". Only surfaced when the headshot
 *                         count adds signal beyond the kill-count tag
 *                         (i.e. HS ≥ 2 and HS < killCount, OR there's no
 *                         "All Headshots" situational already).
 *
 * Returns tags in a deterministic priority order so the MetaRow can take
 * the top N and the most "hype" tag always wins:
 *
 *   ACE > 4K > 3K > 2K
 *     > Clutch > No Scope > Wallbang > Quickscope > 1 Tap > Flashed
 *     > All Headshots > 3 HS > 2 HS
 *
 * Consumers cap at 2–3 chips in the row.
 */

/** Priority ranking for situational tags. Higher = more hype. */
const SITUATIONAL_PRIORITY: Record<string, number> = {
  Clutch: 90,
  "No Scope": 80,
  Wallbang: 70,
  Quickscope: 60,
  "1 Tap": 50,
  Flashed: 40,
  // "All Headshots" ranked alongside the headshot-count family below.
};

/** Situational values we'll surface on the row. Anything emitted by
 *  partner-api outside this set is dropped to avoid noisy long-tail
 *  tags ("Late Round", "Backstab", etc) sneaking onto the chip set. */
const SITUATIONAL_WHITELIST = new Set<string>([
  "Clutch",
  "No Scope",
  "Wallbang",
  "Quickscope",
  "1 Tap",
  "Flashed",
  "All Headshots",
]);

/** Convert raw CS_Kill Count value to its display tag, or null if the
 *  value doesn't represent a multi-kill highlight worth tagging. */
const killCountTag = (raw: string): string | null => {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return null;
  if (n >= 5) return "ACE";
  if (n === 4) return "4K";
  if (n === 3) return "3K";
  if (n === 2) return "2K";
  return null;
};

const killCountPriority = (tag: string): number => {
  if (tag === "ACE") return 1000;
  if (tag === "4K") return 950;
  if (tag === "3K") return 900;
  if (tag === "2K") return 850;
  return 0;
};

const headshotPriority = (tag: string): number => {
  if (tag === "All Headshots") return 35;
  if (tag === "3 HS") return 25;
  if (tag === "2 HS") return 20;
  return 0;
};

const tagPriority = (tag: string): number => {
  const kc = killCountPriority(tag);
  if (kc) return kc;
  const sit = SITUATIONAL_PRIORITY[tag];
  if (sit) return sit;
  const hs = headshotPriority(tag);
  if (hs) return hs;
  return 0;
};

export const getDisplayTags = (clip: PlaylistClip | null): string[] => {
  if (!clip?.metadata) return [];
  const meta = clip.metadata as MetaEntry[];

  const tags = new Set<string>();

  // 1. Kill-count — derived from the raw numeric value rather than a
  //    literal whitelist match. Partner-api emits CS_Kill Count once per
  //    clip; the first hit wins.
  const killRaw = meta.find((m) => m?.key === "CS_Kill Count")?.value ?? "";
  const killTag = killCountTag(killRaw);
  if (killTag) tags.add(killTag);

  // 2. Situational — multi-emit, dedup via the Set. Only whitelisted
  //    values pass through.
  for (const m of meta) {
    if (
      m?.key === "CS_Situational" &&
      m.value &&
      SITUATIONAL_WHITELIST.has(m.value)
    ) {
      tags.add(m.value);
    }
  }

  // 3. Headshot count — only adds signal if "All Headshots" isn't
  //    already in play (which is the stronger phrasing of the same
  //    idea). Skip the chip when HS == killCount AND killCount ≥ 3, the
  //    user can infer "all kills were HS" from a 4K + 4 HS combo, but the
  //    extra chip would crowd the row. Keep when HS is a partial figure
  //    (e.g. 3K with 2 HS — that's distinct context worth showing).
  const hsRaw = meta.find((m) => m?.key === "CS_Headshots")?.value ?? "";
  const hsN = Number.parseInt(hsRaw, 10);
  const killN = Number.parseInt(killRaw, 10);
  const allHsAlready = tags.has("All Headshots");
  const hsFullyOverlaps =
    Number.isFinite(hsN) && Number.isFinite(killN) && hsN >= killN;
  if (Number.isFinite(hsN) && hsN >= 2 && !allHsAlready && !hsFullyOverlaps) {
    tags.add(`${hsN} HS`);
  }

  // Rank by priority (higher first), then alpha as a deterministic
  // tiebreak so two equal-priority tags don't reorder between renders.
  return Array.from(tags).sort((a, b) => {
    const pb = tagPriority(b);
    const pa = tagPriority(a);
    if (pb !== pa) return pb - pa;
    return a.localeCompare(b);
  });
};

export const getViewCount = (clip: PlaylistClip | null): number =>
  clip?.views ?? 0;

export const getClipTitle = (clip: PlaylistClip | null): string =>
  clip?.title ?? "";

export const getClipMp4Url = (clip: PlaylistClip | null): string =>
  clip?.url ?? clip?.clipUrl ?? "";

/**
 * HLS manifest (master.m3u8) for the clip. partner-api ships `clip.hls` on
 * every clip in the branded /playlist response, so this is the preferred
 * source — the MP4 (getClipMp4Url) is the universal fallback for browsers
 * without an HLS-capable path. Mirrors the OPGG/UGG player, which reads the
 * same `clip.hls` field and feeds it to video.js's VHS tech.
 */
export const getClipHlsUrl = (clip: PlaylistClip | null): string =>
  clip?.hls ?? "";

export const getClipThumbnail = (clip: PlaylistClip | null): string =>
  clip?.thumbnailStandardUrl ?? clip?.mobile?.thumbnailStandardUrl ?? "";

export const getClipLengthSec = (clip: PlaylistClip | null): number =>
  clip?.clipLength ?? 0;

/**
 * Round number for the row's ROUND N badge (Figma 1:178).
 *
 * The live payload exposes round on the top-level clip as `roundNumber`
 * (not as a `CS_Round` metadata entry). Falls back to a `CS_Round`
 * metadata read for any historical clips that only carried it inside
 * the metadata bundle.
 */
export const getRoundNumber = (clip: PlaylistClip | null): number | null => {
  const direct = (clip as unknown as { roundNumber?: number } | null)
    ?.roundNumber;
  if (typeof direct === "number" && Number.isFinite(direct)) return direct;
  const raw = readMeta(clip, "CS_Round");
  if (!raw) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
};
